import React, { useState } from 'react';
import { useTranslation, useEventTracker } from '@applyboard/ui-utils';
import {
  Avatar,
  Button,
  Card,
  Checkbox,
  Combobox,
  createForm,
  DatePicker,
  Flex,
  Heading,
  HelpText,
  Link,
  MultiCombobox,
  PhoneInput,
  RadioGroup,
  Spinner,
  Text,
  TextInput,
  useToasts,
} from '@applyboard/crystal-ui';
import {
  CheckCircleFillIcon,
  MagicWandFillIcon,
  SpinnerOutlineIcon,
  ErrorFillIcon,
} from '@applyboard/ui-icons';
import { useParams } from 'react-router-dom';
import { PassportUpload } from './PassportUpload';
import {
  StudentRegistrationRequestForm,
  useCompanyInfo,
  useCountries,
  useLeadSources,
  useStudentRegistrationRequest,
} from '../data';
import ApplyBoardLogoMini from '../assets/ApplyBoardLogoMini.svg';
import { useMarkets } from '../data/markets';

const SUBMIT_EVENT_NAME = 'Student Intake Form - Student Clicked Submit';

const { Field, Form, useSetFieldValues } = createForm<StudentRegistrationRequestForm>();

function PassportAutoFill() {
  const { t } = useTranslation();
  const setFieldValues = useSetFieldValues();
  const [showAutofillUpload, setShowAutofillUpload] = useState(false);
  if (showAutofillUpload)
    return (
      <PassportUpload
        onUploadPassportSuccessful={(values: Partial<StudentRegistrationRequestForm>) => {
          setFieldValues(values);
          setShowAutofillUpload(false);
        }}
      />
    );
  return (
    <Flex direction="column" align="start" gap={4}>
      <Button
        onClick={() => {
          setShowAutofillUpload(true);
        }}
        leadIcon={MagicWandFillIcon}
        intent="magic"
        emphasis="highlighted"
        width="hug"
      >
        {t('passportAutoFill.autofillWithPassport')}
      </Button>
      <HelpText>{t('passportAutoFill.autofillHelpText')}</HelpText>
    </Flex>
  );
}

export function StudentIntakeFormError() {
  return (
    <div style={{ minHeight: '100vh', minWidth: '100vw', display: 'flex' }}>
      <Flex direction="column" justify="center" py={16} align="center" grow={1}>
        <Flex.Item>
          <ErrorFillIcon size="xl" intent="negative" />
        </Flex.Item>
        <Flex.Item>
          <Heading level={1}>Something went wrong while trying to load this page</Heading>
        </Flex.Item>
        <Flex.Item>
          <Text>
            If the problem persists, please contact{' '}
            <Link href="mailto:recruitersupport@applyboard.com">
              recruitersupport@applyboard.com
            </Link>
          </Text>
        </Flex.Item>
      </Flex>
    </div>
  );
}

export function StudentIntakeForm() {
  const { t } = useTranslation();
  const toasts = useToasts();
  const { trackEvent } = useEventTracker();
  const agentId = useParams<{ agent_id: string }>().agent_id;

  const { companyInfo, isLoadingCompanyInfo, isFetchCompanyInfoError } = useCompanyInfo(agentId);

  const { countries, isLoadingCountries } = useCountries();
  const { leadSources, isLoadingLeadSources } = useLeadSources();
  const { markets } = useMarkets();

  const { createStudentRequest, isCreatingStudentRequest, isCreateStudentSuccess } =
    useStudentRegistrationRequest();

  const [showCustomLeadSource, setShowCustomLeadSource] = useState(false);
  const [customLeadSource, setCustomLeadSource] = useState('');
  const [hasSentEvent, setHasSentEvent] = useState(false);

  const SERVICE_OF_INTEREST = [
    { value: '1', label: t('leadSources.programs') },
    { value: '2', label: t('leadSources.scholarships') },
    { value: '3', label: t('leadSources.visaServices') },
    { value: '4', label: t('leadSources.insurance') },
    { value: '5', label: t('leadSources.accommodation') },
    { value: '6', label: t('leadSources.bankAccounts') },
    { value: '7', label: t('leadSources.tourism') },
    { value: '8', label: t('leadSources.other') },
  ];

  if (isLoadingCompanyInfo || isLoadingCountries || isLoadingLeadSources) {
    return (
      <Flex justify="center" py={8}>
        <Spinner size="xl" />
      </Flex>
    );
  }

  if (isFetchCompanyInfoError) {
    return <StudentIntakeFormError />;
  }

  if (!isLoadingCompanyInfo && !companyInfo?.intake_form_enabled) {
    return (
      <DisableForm
        logo={companyInfo?.logo_url ?? undefined}
        companyName={companyInfo?.company_name}
      />
    );
  }

  if (!hasSentEvent) {
    setHasSentEvent(true);
    trackEvent({
      event: 'Student Intake Form - User Accessed Form',
      data: {
        method: window.location === window.parent.location ? 'Direct link' : 'Embedded iframe',
      },
    });
  }

  const DEFAULT_VALUES = {
    email: '',
    phone: '',
    givenName: '',
    middleName: '',
    familyName: '',
    birthDate: '',
    citizenship: '',
    citizenshipCountryCode: '',
    gender: '',
    countryOfInterest: [],
    servicesOfInterest: [],
    referralSource: '',
    agentId: companyInfo?.assigned_staff ? companyInfo?.assigned_staff : parseInt(agentId),
    didDeclaration: false,
    passportNumber: '',
    passportExpiry: '',
  };

  return (
    <Flex justify="center" py={8}>
      <Flex.Item basis={{ xs: '100%', md: '720px' }}>
        <Card>
          <Card.Header>
            <Flex direction="column" gap={6}>
              {isCreateStudentSuccess && (
                <Flex gap={6} justify="center" align="center">
                  <CheckCircleFillIcon size="lg" intent="positive" />
                  <Heading level={1} variant="headlineS">
                    {t('thankYouMessage')}
                  </Heading>
                </Flex>
              )}
              <Flex gap={6}>
                <Flex.Item>
                  <Avatar
                    alt={companyInfo?.company_name}
                    emphasis="contained"
                    image={companyInfo?.logo_url ?? undefined}
                    initials={
                      companyInfo?.logo_url
                        ? undefined
                        : (companyInfo?.company_name.match(/\b\w/g) ?? []).join('').slice(0, 3)
                    }
                    intent="primary"
                    size="xl"
                  />
                </Flex.Item>
                <Flex.Item>
                  <Heading
                    level={isCreateStudentSuccess ? 2 : 1}
                    variant={isCreateStudentSuccess ? 'titleS' : 'titleM'}
                  >
                    {companyInfo?.company_name}
                  </Heading>
                  <Text variant="bodyM">
                    {isCreateStudentSuccess
                      ? t('successMessage', { companyName: companyInfo?.company_name })
                      : t('subtitle')}
                  </Text>
                </Flex.Item>
              </Flex>
            </Flex>
          </Card.Header>
          {!isCreateStudentSuccess && (
            <>
              <Form
                defaultValues={DEFAULT_VALUES}
                id="student-intake-form"
                onSubmit={fieldValues => {
                  let formData = {
                    ...fieldValues,
                    citizenship:
                      countries.find(country => country.code === fieldValues.citizenshipCountryCode)
                        ?.name ?? fieldValues.citizenshipCountryCode,
                    birthDate: fieldValues.birthDate.slice(0, 10),
                    passportExpiry: fieldValues.passportExpiry.slice(0, 10),
                  };
                  if (showCustomLeadSource) {
                    formData = {
                      ...formData,
                      customLeadSource,
                    };
                  }
                  createStudentRequest(
                    formData,
                    () => {
                      trackEvent({
                        event: SUBMIT_EVENT_NAME,
                        data: {
                          agent_id: agentId,
                          fields: formData,
                          student_created: true,
                        },
                      });
                    },
                    error => {
                      const errorMessage = (error as Error).message;

                      let errorCode;

                      try {
                        errorCode = JSON.parse(errorMessage)?.errorCode;
                      } catch {
                        errorCode = '';
                      }

                      const companyName = companyInfo?.company_name;

                      trackEvent({
                        event: SUBMIT_EVENT_NAME,
                        data: {
                          agent_id: agentId,
                          fields: formData,
                          student_created: false,
                        },
                      });

                      if (errorCode) {
                        if (errorCode === 'EMAIL_ALREADY_EXISTS') {
                          toasts.negative(
                            new Error(t('form.submitError.duplicateStudent', { companyName })),
                          );
                          return;
                        }
                      }
                      toasts.negative(new Error(t('form.submitError.generic', { companyName })));
                    },
                  );
                }}
              >
                <Flex direction="column" gap={8} py={8}>
                  <Flex direction="column" gap={4}>
                    <Heading level={2} variant="titleS">
                      {t('contactInformation')}
                    </Heading>
                    <Field
                      as={TextInput}
                      label={t('form.email')}
                      name="email"
                      required={t('form.requiredMessage')}
                    />
                    <Field
                      as={PhoneInput}
                      label={t('form.phoneNumber')}
                      name="phone"
                      required={t('form.requiredMessage')}
                    />
                  </Flex>

                  <Flex direction="column" gap={4}>
                    <Heading level={2} variant="titleS">
                      {t('personalInformation')}
                    </Heading>
                    <PassportAutoFill />
                    <Field
                      as={TextInput}
                      name="givenName"
                      label={t('form.givenName')}
                      required={t('form.requiredMessage')}
                    />
                    <Field as={TextInput} name="middleName" label={t('form.middleName')} />
                    <Field
                      as={TextInput}
                      name="familyName"
                      label={t('form.familyName')}
                      required={t('form.requiredMessage')}
                    />
                    <Field
                      as={DatePicker}
                      granularity="day"
                      name="birthDate"
                      label={t('form.dateOfBirth')}
                      maxDate={new Date().toISOString()}
                      minDate="1900-01-01T00:00:00.000Z"
                      size="md"
                      required={t('form.requiredMessage')}
                    />
                    <Field
                      as={Combobox}
                      name="citizenshipCountryCode"
                      label={t('form.countryOfCitizenship')}
                      placeholder={t('form.selectCountry')}
                      required={t('form.requiredMessage')}
                      disabled={isLoadingCountries}
                    >
                      {countries.map(country => (
                        <Combobox.Option
                          label={country.name}
                          value={country.code}
                          key={country.code}
                        />
                      ))}
                    </Field>
                    <Field
                      as={RadioGroup}
                      name="gender"
                      label={t('form.gender')}
                      required={t('form.requiredMessage')}
                    >
                      <Flex gap={6}>
                        <RadioGroup.Option label={t('form.male')} value="male" />
                        <RadioGroup.Option label={t('form.female')} value="female" />
                      </Flex>
                    </Field>
                    <Field as={TextInput} name="passportNumber" label={t('form.passportNumber')} />
                    <Field
                      as={DatePicker}
                      granularity="day"
                      name="passportExpiry"
                      label={t('form.passportExpiryDate')}
                      maxDate="2100-01-01T00:00:00.000Z"
                      minDate={new Date().toISOString()}
                      size="md"
                    />
                  </Flex>

                  <Flex direction="column" gap={4}>
                    <Heading level={2} variant="titleS">
                      {t('additionalInformation')}
                    </Heading>
                    <Field
                      as={MultiCombobox}
                      name="countryOfInterest"
                      label={t('form.countryOfInterest')}
                      placeholder={t('form.selectCountry')}
                      required={t('form.requiredMessage')}
                      disabled={isLoadingCountries}
                    >
                      {markets.map(market => (
                        <MultiCombobox.Option
                          label={market.displayName}
                          value={market.id}
                          key={market.id}
                        />
                      ))}
                    </Field>
                    <Field
                      as={MultiCombobox}
                      name="servicesOfInterest"
                      label={t('form.servicesOfInterest')}
                      placeholder={t('form.selectServices')}
                      required={t('form.requiredMessage')}
                    >
                      {SERVICE_OF_INTEREST.map(service => (
                        <MultiCombobox.Option
                          label={service.label}
                          value={service.value}
                          key={service.value}
                        />
                      ))}
                    </Field>
                    <Field
                      as={Combobox}
                      name="referralSource"
                      label={t('form.referralSource')}
                      placeholder={t('form.selectReferralSource')}
                      disabled={isLoadingLeadSources}
                      onChange={leadSorce => setShowCustomLeadSource(leadSorce === 'other')}
                      required={t('form.requiredMessage')}
                    >
                      {leadSources.map(source => (
                        <Combobox.Option
                          label={source.displayName}
                          value={source.name}
                          key={source.name}
                        />
                      ))}
                    </Field>
                    {showCustomLeadSource && (
                      <TextInput
                        aria-label={t('form.customLeadSource')}
                        placeholder={t('form.customLeadSource')}
                        value={customLeadSource}
                        onChange={setCustomLeadSource}
                      />
                    )}
                    <Field
                      as={Checkbox}
                      name="didDeclaration"
                      label={
                        <>
                          <Text>{t('declariationText')}</Text>
                          <Link href="https://www.applyboard.com/privacy-and-cookie-policy">
                            {t('privacyPolicy')}
                          </Link>
                        </>
                      }
                      required={t('form.requiredMessage')}
                    />
                  </Flex>
                </Flex>
              </Form>
              <footer
                css={{
                  padding: '8px',
                }}
              >
                <Flex justify="between" align="center">
                  <Flex align="center" gap={2}>
                    <img src={ApplyBoardLogoMini} alt="ApplyBoard" />
                    <Text>{t('poweredBy')}</Text>
                  </Flex>
                  <Button
                    emphasis="contained"
                    intent="primary"
                    width="hug"
                    type="submit"
                    form="student-intake-form"
                    disabled={isCreatingStudentRequest}
                    trailIcon={isCreatingStudentRequest ? SpinnerOutlineIcon : undefined}
                  >
                    {t('form.submit')}
                  </Button>
                </Flex>
              </footer>
            </>
          )}
        </Card>
      </Flex.Item>
    </Flex>
  );
}

function DisableForm({
  logo,
  companyName,
}: {
  logo: string | undefined;
  companyName: string | undefined;
}) {
  return (
    <Flex justify="center" py={8}>
      <Flex.Item basis={{ xs: '100%', md: '720px' }}>
        <Card>
          <Card.Header>
            <Flex direction="column" gap={6}>
              <Flex gap={6} justify="center" align="center">
                <CheckCircleFillIcon size="lg" intent="positive" />
                <Heading level={1} variant="headlineS">
                  This form is disabled
                </Heading>
              </Flex>
              <Flex gap={6}>
                <Flex.Item>
                  <Avatar
                    alt={companyName}
                    emphasis="contained"
                    image={logo ?? undefined}
                    initials={
                      logo ? undefined : (companyName?.match(/\b\w/g) ?? []).join('').slice(0, 3)
                    }
                    intent="primary"
                    size="xl"
                  />
                </Flex.Item>
                <Flex.Item>
                  <Heading level={1} variant="titleM">
                    {companyName}
                  </Heading>
                  <Text variant="bodyM">
                    The owner of this form will not be taking submissions at this time. Please
                    contact the owner of this form, if you think this is a mistake.
                  </Text>
                </Flex.Item>
              </Flex>
            </Flex>
          </Card.Header>
        </Card>
      </Flex.Item>
    </Flex>
  );
}
